import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TextInputComponent } from 'src/app/shared/components/inputs/text-input/text-input.component';
import { ButtonComponent } from 'src/app/shared/components/inputs/button/button.component';
import { TextInput, SelectInput } from 'src/utils/models/input.interface';
import { HttpService } from 'src/app/service/http/http.service';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ToastrService } from 'ngx-toastr';
import { UploadCardComponent, ExistingFile } from 'src/app/shared/components/inputs/upload-card/upload-card.component';
import { SelectInputComponent } from 'src/app/shared/components/inputs/select-input/select-input.component';
import { switchMap } from 'rxjs';
import { DatasetItem, PaginatedResponse, SeriesItem } from '../../../models/lakehouse.model';

interface DatasetDuplicateTemplate {
  name: string;
  description?: string | null;
  contractId?: string;
  fileName: string;
  temporalColumn: string;
  series: { name: string; unit: string }[];
}

export interface AddSeriesModalData {
  sourceId: string;
  sourceName?: string;
}

export interface ColumnVariable {
  name: string;
  unit?: string;
}

export interface ManualSeriesRow {
  id: string;
  name: string;
  unit: string;
}

type RegistrationMode = 'csv' | 'manual';
type StartView = 'choose' | 'csv' | 'duplicate';

@Component({
  selector: 'app-add-series-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextInputComponent,
    ButtonComponent,
    MatIconModule,
    MatSlideToggleModule,
    UploadCardComponent,
    SelectInputComponent
  ],
  templateUrl: './add-series-modal.component.html',
  styleUrls: ['./add-series-modal.component.scss']
})
export class AddSeriesModalComponent implements OnInit {
  step: number = 1;
  form: FormGroup;
  seriesNameInput: TextInput;
  descriptionInput: TextInput;
  contractIdInput: TextInput;
  temporalColumnSelect: SelectInput;
  variablesSelect: SelectInput;
  registrationMode: RegistrationMode | null = null;
  csvFile: File | null = null;
  existingFile: ExistingFile | null = null;
  manualSeries: ManualSeriesRow[] = [];
  processingFile: File | null = null;
  processingExistingFile: ExistingFile | null = null;
  temporalColumnInput: TextInput;
  private manualSeriesSeq = 0;
  isLoading: boolean = false;
  isUploading: boolean = false;
  columns: string[] = [];
  columnVariables: ColumnVariable[] = [];
  temporalColumn: string = '';
  selectedVariablesForUnits: string[] = [];
  variablesWithUnits: { [key: string]: string } = {};
  startView: StartView = 'choose';
  loadingDatasets = false;
  loadingDuplicateSeries = false;
  sourceDatasets: DatasetItem[] = [];
  sourceDatasetSelect: SelectInput | null = null;
  selectedSourceDatasetId = '';
  /** file_name de origem. O nome exibido continua editável; a trava vale só no avanço e no envio. */
  private duplicateSourceFileName = '';
  /** Quando true, aplica a mesma unidade (do primeiro campo preenchido) a todas as variáveis. */
  applySameUnitToAll = false;

  constructor(
    public dialogRef: MatDialogRef<AddSeriesModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddSeriesModalData,
    private fb: FormBuilder,
    private httpService: HttpService,
    private toastr: ToastrService
  ) {
    this.form = this.fb.group({
      seriesName: [''],
      description: [''],
      contractId: [''],
      temporalColumn: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.seriesNameInput = {
      value: '',
      label: 'Nome da dataset',
      placeholder: 'Se o nome estiver vazio, o nome do arquivo será utilizado',
      maxLength: '60',
      required: { active: false }
    };

    this.descriptionInput = {
      value: '',
      label: 'Descrição',
      placeholder: 'Digite a descrição',
      maxLength: '500',
      required: { active: false }
    };

    this.contractIdInput = {
      value: '',
      label: 'ID do contrato',
      placeholder: 'Ex.: dc_meu_arquivo (opcional)',
      maxLength: '120',
      required: { active: false }
    };

    this.temporalColumnInput = {
      value: '',
      label: 'Variável temporal',
      placeholder: 'Ex.: date',
      maxLength: '120',
      required: { active: true }
    };
  }

  startManualRegistration(): void {
    if (this.isUploading) {
      return;
    }

    this.duplicateSourceFileName = '';
    this.startView = 'choose';
    this.registrationMode = 'manual';
    this.csvFile = null;
    this.existingFile = null;
    this.columns = [];
    this.seriesNameInput = {
      ...this.seriesNameInput,
      placeholder: 'Digite o nome do dataset',
      required: { active: true }
    };
    if (this.manualSeries.length === 0) {
      this.manualSeries = [this.createManualSeriesRow()];
    }
    this.step = 2;
  }

  chooseCsvRegistration(): void {
    this.duplicateSourceFileName = '';
    this.registrationMode = null;
    this.csvFile = null;
    this.existingFile = null;
    this.columns = [];
    this.startView = 'csv';
  }

  startDuplicateRegistration(): void {
    if (this.isUploading) {
      return;
    }

    this.registrationMode = null;
    this.startView = 'duplicate';
    this.selectedSourceDatasetId = '';
    this.loadSourceDatasets();
  }

  onSourceDatasetChange(event: { option?: string } | null): void {
    this.selectedSourceDatasetId = event?.option ?? '';
  }

  onFilesSelected(event: File): void {
    if (event) {
      this.duplicateSourceFileName = '';
      this.startView = 'csv';
      this.registrationMode = 'csv';
      this.csvFile = event;
      this.seriesNameInput = {
        ...this.seriesNameInput,
        placeholder: 'Se o nome estiver vazio, o nome do arquivo será utilizado',
        required: { active: false }
      };
      const nowIso = new Date().toISOString();
      const userName = localStorage.getItem('name') || '';

      this.existingFile = {
        _id: 'local-file',
        fileName: event.name,
        createdAt: nowIso,
        fileSize: event.size,
        fileDownloadLink: '',
        updatedBy: userName
      };

      // Se o nome da série estiver vazio, usa o nome do arquivo
      if (!this.form.get('seriesName')?.value?.trim()) {
        const fileNameWithoutExtension = event.name.replace(/\.[^/.]+$/, '');
        this.form.get('seriesName')?.setValue(fileNameWithoutExtension);
      }
      // Faz upload e busca colunas
      this.uploadFileAndGetColumns();
    }
  }

  onNext(): void {
    if (this.step === 1) {
      if (this.startView === 'duplicate') {
        this.confirmDuplicateSource();
        return;
      }
      if (!this.csvFile) {
        this.toastr.show('Por favor, selecione um arquivo', 'Aviso', {
          timeOut: 6000,
          messageClass: 'warning'
        });
        return;
      }
      // Se já tem colunas, avança para próxima etapa
      if (this.columns.length > 0) {
        this.step = 2;
      } else {
        // Se ainda não tem colunas, faz upload
        this.uploadFileAndGetColumns();
      }
    } else if (this.step === 2) {
      const datasetName = this.form.get('seriesName')?.value?.trim();
      if (this.registrationMode === 'manual' && !datasetName) {
        this.toastr.show('Por favor, informe o nome do dataset', 'Aviso', {
          timeOut: 6000,
          messageClass: 'warning'
        });
        return;
      }
      if (datasetName && this.isSameAsSourceDataset(datasetName)) {
        this.warnSameDatasetName();
        return;
      }
      this.step = 3;
      if (this.registrationMode !== 'manual') {
        this.initializeTemporalColumnSelect();
      }
    } else if (this.step === 3) {
      if (this.registrationMode === 'manual') {
        this.temporalColumn = (this.form.get('temporalColumn')?.value || '').trim();
        if (!this.temporalColumn) {
          this.toastr.show('Por favor, informe a variável temporal', 'Aviso', {
            timeOut: 6000,
            messageClass: 'warning'
          });
          return;
        }
        if (this.manualSeries.length === 0) {
          this.manualSeries = [this.createManualSeriesRow()];
        }
        this.step = 4;
        return;
      }
      // Valida se a variável temporal foi selecionada
      if (!this.temporalColumn) {
        this.toastr.show('Por favor, selecione a variável temporal', 'Aviso', {
          timeOut: 6000,
          messageClass: 'warning'
        });
        return;
      }
      this.step = 4;
      this.initializeVariablesSelect();
    } else if (this.step === 4) {
      if (this.registrationMode === 'manual') {
        const validationMessage = this.validateManualSeries();
        if (validationMessage) {
          this.toastr.show(validationMessage, 'Aviso', {
            timeOut: 6000,
            messageClass: 'warning'
          });
          return;
        }
        this.syncManualSeriesToColumnsConfig();
        this.step = 5;
        return;
      }
      // Valida se pelo menos uma variável foi selecionada para unidade
      if (this.selectedVariablesForUnits.length === 0) {
        this.toastr.show('Por favor, selecione pelo menos uma variável para adicionar unidade de medida', 'Aviso', {
          timeOut: 6000,
          messageClass: 'warning'
        });
        return;
      }
      // Valida se todas as variáveis selecionadas têm unidade
      const allHaveUnits = this.selectedVariablesForUnits.every(v => 
        this.variablesWithUnits[v] && this.variablesWithUnits[v].trim() !== ''
      );
      if (!allHaveUnits) {
        this.toastr.show('Por favor, preencha a unidade de medida para todas as variáveis selecionadas', 'Aviso', {
          timeOut: 6000,
          messageClass: 'warning'
        });
        return;
      }
      this.step = 5;
    }
  }

  onBack(): void {
    if (this.step === 1 && this.startView !== 'choose') {
      this.returnToStartChoices();
      return;
    }
    if (this.step > 1) {
      this.step--;
    }
  }

  onFinish(): void {
    // Valida se todas as unidades foram preenchidas (opcional)
    // Por enquanto, apenas submete
    this.submitSeries();
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  onFileDelete(): void {
    this.registrationMode = null;
    this.csvFile = null;
    this.existingFile = null;
    this.columns = [];
    this.columnVariables = [];
    this.temporalColumn = '';
    this.selectedVariablesForUnits = [];
    this.variablesWithUnits = {};
    this.step = 1;
  }

  onProcessingFileSelected(event: File): void {
    if (!event) {
      return;
    }
    this.processingFile = event;
    this.processingExistingFile = {
      _id: 'processing-file',
      fileName: event.name,
      createdAt: new Date().toISOString(),
      fileSize: event.size,
      fileDownloadLink: '',
      updatedBy: localStorage.getItem('name') || ''
    };
  }

  onProcessingFileDelete(): void {
    this.processingFile = null;
    this.processingExistingFile = null;
  }

  addManualSeries(): void {
    this.manualSeries = [...this.manualSeries, this.createManualSeriesRow()];
  }

  removeManualSeries(index: number): void {
    if (this.manualSeries.length <= 1) {
      return;
    }
    this.manualSeries = this.manualSeries.filter((_, rowIndex) => rowIndex !== index);
  }

  updateManualSeriesName(index: number, name: string): void {
    this.manualSeries[index].name = name;
  }

  updateManualSeriesUnit(index: number, unit: string): void {
    this.manualSeries[index].unit = unit;
  }

  onManualTemporalColumnChange(value: string): void {
    this.temporalColumn = (value || '').trim();
    this.form.get('temporalColumn')?.setValue(value || '');
  }

  updateVariableUnit(columnName: string, unit: string): void {
    this.variablesWithUnits[columnName] = unit;
    if (this.applySameUnitToAll && this.selectedVariablesForUnits.length > 1) {
      this.selectedVariablesForUnits.forEach(v => {
        this.variablesWithUnits[v] = unit;
      });
    }
  }

  /** Retorna true se há mais de 1 variável selecionada e pelo menos uma com unidade preenchida (para exibir o toggle). */
  get hasMultipleVariablesWithUnitOption(): boolean {
    if (this.selectedVariablesForUnits.length <= 1) return false;
    return this.selectedVariablesForUnits.some(
      v => this.variablesWithUnits[v] != null && this.variablesWithUnits[v].trim() !== ''
    );
  }

  onApplySameUnitToAllChange(checked: boolean): void {
    this.applySameUnitToAll = checked;
    if (checked) {
      const firstUnit = this.getFirstFilledUnit();
      this.selectedVariablesForUnits.forEach(v => {
        this.variablesWithUnits[v] = firstUnit;
      });
    } else {
      const firstVar = this.getFirstVariableInList();
      this.selectedVariablesForUnits.forEach(v => {
        if (v !== firstVar) {
          this.variablesWithUnits[v] = '';
        }
      });
    }
  }

  /** Primeira variável na lista (ordem exibida) para vincular o toggle. */
  getFirstVariableInList(): string | null {
    const available = this.getAvailableVariablesForUnits();
    return available.length > 0 ? available[0] : null;
  }

  private getFirstFilledUnit(): string {
    const firstVarWithUnit = this.getAvailableVariablesForUnits().find(
      v => this.isVariableSelected(v) && this.variablesWithUnits[v]?.trim() !== ''
    );
    return firstVarWithUnit ? (this.variablesWithUnits[firstVarWithUnit] ?? '').trim() : '';
  }

  onTemporalColumnChange(event: any): void {
    if (event && event.option) {
      this.temporalColumn = event.option;
      this.form.get('temporalColumn')?.setValue(event.option);
    }
  }

  onVariablesForUnitsChange(event: any): void {
    if (event && event.option) {
      // Se for array (seleção múltipla), atualiza a lista
      if (Array.isArray(event.option)) {
        this.selectedVariablesForUnits = event.option;
      } else {
        // Se for string única, converte para array
        this.selectedVariablesForUnits = event.option ? [event.option] : [];
      }
    }
  }

  initializeTemporalColumnSelect(): void {
    this.temporalColumnSelect = {
      value: this.temporalColumn || '',
      label: 'Variável Temporal',
      placeholder: 'Selecione a variável temporal',
      options: this.columns.map(col => ({
        value: col,
        viewValue: col
      }))
    };
  }

  initializeVariablesSelect(): void {
    // Remove a variável temporal da lista
    const availableVariables = this.columns.filter(col => col !== this.temporalColumn);
    this.variablesSelect = {
      value: '',
      label: 'Variáveis para Unidade de Medida',
      placeholder: 'Selecione as variáveis que terão unidade de medida',
      options: availableVariables.map(col => ({
        value: col,
        viewValue: col
      }))
    };
  }

  getAvailableVariablesForUnits(): string[] {
    return this.columns.filter(col => col !== this.temporalColumn);
  }

  isVariableSelected(variable: string): boolean {
    return this.selectedVariablesForUnits.includes(variable);
  }

  /** True quando há mais de uma variável disponível (para exibir o checkbox "Selecionar todas"). */
  get hasMultipleVariablesForUnits(): boolean {
    return this.getAvailableVariablesForUnits().length > 1;
  }

  /** True quando todas as variáveis disponíveis estão selecionadas. */
  get allVariablesSelected(): boolean {
    const available = this.getAvailableVariablesForUnits();
    return available.length > 0 && available.every(v => this.isVariableSelected(v));
  }

  onSelectAllVariablesChange(checked: boolean): void {
    const available = this.getAvailableVariablesForUnits();
    if (checked) {
      this.selectedVariablesForUnits = [...available];
      available.forEach(v => {
        if (!this.variablesWithUnits[v]) {
          this.variablesWithUnits[v] = '';
        }
      });
    } else {
      available.forEach(v => delete this.variablesWithUnits[v]);
      this.selectedVariablesForUnits = [];
    }
  }

  toggleVariableSelection(variable: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedVariablesForUnits.includes(variable)) {
        this.selectedVariablesForUnits.push(variable);
        // Inicializa a unidade como vazia se não existir
        if (!this.variablesWithUnits[variable]) {
          this.variablesWithUnits[variable] = '';
        }
      }
    } else {
      const index = this.selectedVariablesForUnits.indexOf(variable);
      if (index > -1) {
        this.selectedVariablesForUnits.splice(index, 1);
        // Remove a unidade quando desmarcar
        delete this.variablesWithUnits[variable];
      }
    }
  }

  getSeriesName(): string {
    const name = this.form.get('seriesName')?.value?.trim();
    if (name) {
      return name;
    }
    if (this.csvFile) {
      return this.csvFile.name.replace(/\.[^/.]+$/, '');
    }
    return 'Série sem nome';
  }

  getDescription(): string {
    return this.form.get('description')?.value?.trim() || '';
  }

  getContractId(): string {
    return this.form.get('contractId')?.value?.trim() || '';
  }

  get showNextButton(): boolean {
    return !(this.step === 1 && this.startView === 'choose');
  }

  getButtonLabel(): string {
    if (this.step === 5) {
      return 'Finalizar';
    }
    return 'Próximo';
  }

  getButtonLoading(): boolean {
    if (this.step === 1) {
      return this.isUploading || this.loadingDuplicateSeries;
    }
    if (this.step === 5) {
      return this.isLoading;
    }
    return false;
  }

  isNextButtonDisabled(): boolean {
    if (this.step === 1) {
      if (this.startView === 'duplicate') {
        return !this.selectedSourceDatasetId || this.loadingDatasets || this.loadingDuplicateSeries;
      }
      return !this.csvFile || this.isUploading;
    }
    if (this.step === 2) {
      return !this.form.get('seriesName')?.value?.trim();
    }
    if (this.step === 3) {
      if (this.registrationMode === 'manual') {
        return !(this.form.get('temporalColumn')?.value || '').trim();
      }
      return !this.temporalColumn;
    }
    if (this.step === 4) {
      if (this.registrationMode === 'manual') {
        return this.validateManualSeries() !== null;
      }
      return this.selectedVariablesForUnits.length === 0 || this.selectedVariablesForUnits.some(variable => !this.variablesWithUnits[variable]);
    }
    return false;
  }
  
  private uploadFileAndGetColumns(): void {
    if (!this.csvFile) {
      return;
    }

    this.isUploading = true;
    const projectId = sessionStorage.getItem('projectId');
    
    if (!projectId) {
      this.toastr.show('ProjectId não encontrado. Faça login novamente.', 'Erro', {
        timeOut: 6000,
        messageClass: 'error'
      });
      this.isUploading = false;
      return;
    }

    const formData = new FormData();
    formData.append('file', this.csvFile);

    this.httpService.genericPost<string[]>('series/parse-csv-columns', formData, '').subscribe({
      next: (columns: string[]) => {
        this.isUploading = false;
        if (this.startView !== 'csv') {
          return;
        }
        this.columns = columns;
        this.initializeColumnVariables(columns);
      },
      error: (err) => {
        this.isUploading = false;
        const errorMessage = err?.error?.message || err?.message || 'Erro ao processar arquivo. Tente novamente.';
        this.toastr.show(errorMessage, 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
      }
    });
  }

  private initializeColumnVariables(columns: string[]): void {
    this.columnVariables = columns.map(column => ({
      name: column,
      unit: ''
    }));
  }

  private submitSeries(): void {
    this.isLoading = true;

    const sourceFile = this.registrationMode === 'manual' ? this.processingFile : this.csvFile;
    if (this.registrationMode !== 'manual' && !sourceFile) {
      this.toastr.show('Arquivo não encontrado. Por favor, selecione um arquivo novamente.', 'Erro', {
        timeOut: 6000,
        messageClass: 'error'
      });
      this.isLoading = false;
      return;
    }

    const datasetName = this.form.get('seriesName')?.value?.trim()
      || sourceFile?.name.replace(/\.[^/.]+$/, '')
      || 'Dataset sem nome';
    if (this.isSameAsSourceDataset(datasetName)) {
      this.warnSameDatasetName();
      this.isLoading = false;
      return;
    }
    const description = this.form.get('description')?.value?.trim();
    const contractId = this.form.get('contractId')?.value?.trim();
    const fileName = sourceFile
      ? sourceFile.name.replace(/\.[^/.]+$/, '')
      : this.slugifyDatasetName(datasetName);

    const datasetPayload = {
      name: datasetName,
      file_name: fileName,
      source_id: this.data.sourceId,
      ...(description ? { description } : {}),
      ...(contractId ? { contract_id: contractId } : {}),
    };

    this.httpService.genericPost<{ _id: string }>('datasets', datasetPayload, '').pipe(
      switchMap((dataset) => {
        const formData = new FormData();
        if (sourceFile) {
          formData.append('file', sourceFile);
        }
        formData.append('dataset_id', dataset._id);
        formData.append('temporal_column', this.temporalColumn);

        if (Object.keys(this.variablesWithUnits).length > 0) {
          formData.append('columns_config', JSON.stringify(this.variablesWithUnits));
        }

        return this.httpService.genericPost<any>('series', formData, '');
      })
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        const series = Array.isArray(response) ? response[0] : response;
        this.dialogRef.close({
          success: true,
          data: series
        });
        const successMessage = sourceFile
          ? 'Dataset adicionado com sucesso. Você pode acompanhar o status das séries na lista.'
          : 'Dataset adicionado com sucesso.';
        this.toastr.show(successMessage, 'Sucesso', {
          timeOut: 6000,
          messageClass: 'success'
        });
      },
      error: (err) => {
        this.isLoading = false;
        const errorMessage = err?.error?.message || err?.message || 'Erro ao adicionar dataset. Tente novamente.';
        this.toastr.show(errorMessage, 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
      }
    });
  }

  private loadSourceDatasets(): void {
    this.loadingDatasets = true;
    this.sourceDatasets = [];
    this.sourceDatasetSelect = null;
    this.httpService.genericGetParams<PaginatedResponse<DatasetItem>>('datasets', [
      { param: 'source_id', value: this.data.sourceId }
    ]).subscribe({
      next: (response) => {
        this.sourceDatasets = [...(response.rows ?? [])].sort((left, right) =>
          left.name.localeCompare(right.name, 'pt-BR')
        );
        this.sourceDatasetSelect = {
          value: '',
          label: 'Dataset de origem',
          placeholder: 'Selecione o dataset',
          options: this.sourceDatasets.map(dataset => ({
            value: dataset._id,
            viewValue: dataset.name
          }))
        };
        this.loadingDatasets = false;
      },
      error: () => {
        this.loadingDatasets = false;
        this.toastr.show('Erro ao buscar datasets', 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
      }
    });
  }

  private confirmDuplicateSource(): void {
    if (this.loadingDuplicateSeries) {
      return;
    }

    const dataset = this.sourceDatasets.find(item => item._id === this.selectedSourceDatasetId);
    if (!dataset) {
      this.toastr.show('Selecione o dataset de origem', 'Aviso', {
        timeOut: 6000,
        messageClass: 'warning'
      });
      return;
    }

    this.loadingDuplicateSeries = true;
    this.httpService.genericGetParams<PaginatedResponse<SeriesItem>>('series', [
      { param: 'dataset_id', value: dataset._id }
    ]).subscribe({
      next: (response) => {
        this.loadingDuplicateSeries = false;
        const series = response.rows ?? [];
        this.applyDuplicateTemplate({
          name: dataset.name,
          description: dataset.description,
          contractId: dataset.contract_id,
          fileName: dataset.file_name,
          temporalColumn: series.find(item => item.temporal_column?.trim())?.temporal_column?.trim() ?? '',
          series: series.map(item => ({ name: item.name, unit: item.unit ?? '' }))
        });
      },
      error: () => {
        this.loadingDuplicateSeries = false;
        this.toastr.show('Erro ao buscar séries do dataset', 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
      }
    });
  }

  private applyDuplicateTemplate(template: DatasetDuplicateTemplate): void {
    this.registrationMode = 'manual';
    this.startView = 'choose';
    this.csvFile = null;
    this.existingFile = null;
    this.columns = [];
    this.processingFile = null;
    this.processingExistingFile = null;
    this.duplicateSourceFileName = template.fileName.trim().toLowerCase();
    this.seriesNameInput = {
      ...this.seriesNameInput,
      value: template.name,
      disabled: false,
      readonly: false,
      placeholder: 'Digite o nome do dataset',
      required: { active: true }
    };
    this.descriptionInput = {
      ...this.descriptionInput,
      value: template.description ?? '',
      disabled: false,
      readonly: false
    };
    this.contractIdInput = {
      ...this.contractIdInput,
      value: template.contractId ?? '',
      disabled: false,
      readonly: false
    };
    this.temporalColumn = template.temporalColumn;
    this.temporalColumnInput = {
      ...this.temporalColumnInput,
      value: template.temporalColumn,
      disabled: false,
      readonly: false
    };
    this.form.enable();
    this.form.patchValue({
      seriesName: template.name,
      description: template.description ?? '',
      contractId: template.contractId ?? '',
      temporalColumn: template.temporalColumn
    });
    this.manualSeries = template.series.length > 0
      ? template.series.map(series => this.createManualSeriesRow(series.name, series.unit))
      : [this.createManualSeriesRow()];
    this.step = 2;
  }

  private isSameAsSourceDataset(datasetName: string): boolean {
    if (!this.duplicateSourceFileName) {
      return false;
    }
    return this.slugifyDatasetName(datasetName) === this.duplicateSourceFileName;
  }

  private warnSameDatasetName(): void {
    this.toastr.show('Altere o nome do dataset. Este nome já pertence ao dataset de origem.', 'Aviso', {
      timeOut: 6000,
      messageClass: 'warning'
    });
  }

  private createManualSeriesRow(name = '', unit = ''): ManualSeriesRow {
    this.manualSeriesSeq += 1;
    return { id: `manual-series-${this.manualSeriesSeq}`, name, unit };
  }

  private validateManualSeries(): string | null {
    const rows = this.manualSeries.map(row => ({
      name: row.name.trim(),
      unit: row.unit.trim()
    }));

    if (rows.length === 0 || rows.some(row => !row.name || !row.unit)) {
      return 'Informe o nome e a unidade de cada série';
    }

    const temporalColumn = (this.temporalColumn || this.form.get('temporalColumn')?.value || '').trim();
    if (rows.some(row => row.name === temporalColumn)) {
      return 'O nome da série precisa ser diferente da variável temporal';
    }

    const uniqueNames = new Set(rows.map(row => row.name));
    if (uniqueNames.size !== rows.length) {
      return 'Os nomes das séries precisam ser únicos';
    }

    return null;
  }

  private syncManualSeriesToColumnsConfig(): void {
    this.selectedVariablesForUnits = this.manualSeries.map(row => row.name.trim());
    this.variablesWithUnits = {};
    this.manualSeries.forEach(row => {
      this.variablesWithUnits[row.name.trim()] = row.unit.trim();
    });
  }

  private returnToStartChoices(): void {
    this.startView = 'choose';
    this.selectedSourceDatasetId = '';
    this.registrationMode = null;
    this.csvFile = null;
    this.existingFile = null;
    this.columns = [];
    this.columnVariables = [];
  }

  private slugifyDatasetName(name: string): string {
    const slug = name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return slug || 'dataset';
  }
}

